import type { FileSystemTree } from "@webcontainer/api";
import type { File } from "schemas/generated/file";

import generate from "@babel/generator";
import * as parser from "@babel/parser";
import traverse from "@babel/traverse";
import * as t from "@babel/types";

export const findPageFileFromPath = (path: string, files: File[]) => {
  return files.find((f) => f.pageRoute === path && f.fileType === "page");
};

/**
 * Recursively transform a FileSystemTree to inject source metadata into matching files
 * @param params - Configuration object for the transformation
 * @param params.fileTree - The FileSystemTree to transform
 * @param params.matchPatterns - Array of patterns to match file extensions (defaults to ['.ts', '.tsx'])
 * @param params.currentPath - The current path context for building full file paths
 * @returns A new transformed FileSystemTree
 */
export function transformFileTree({
  fileTree,
  matchPatterns,
  currentPath,
}: {
  fileTree: FileSystemTree;
  matchPatterns?: string[];
  currentPath?: string;
}): FileSystemTree {
  const transformedTree: FileSystemTree = {};

  for (const [name, entry] of Object.entries(fileTree)) {
    const fullPath = currentPath ? `${currentPath}/${name}` : name;

    if ("file" in entry) {
      let shouldTransform = false;
      if (matchPatterns) {
        shouldTransform = matchPatterns?.some((pattern) =>
          name.endsWith(pattern),
        );
      }

      if (
        "contents" in entry.file &&
        shouldTransform &&
        typeof entry.file.contents === "string"
      ) {
        const transformedContent = injectSourceMetadata(
          entry.file.contents,
          fullPath,
        );
        transformedTree[name] = {
          file: {
            ...entry.file,
            contents: transformedContent,
          },
        };
      } else {
        // Keep other files unchanged (including symlinks and non-string contents)
        transformedTree[name] = entry;
      }
    } else if ("directory" in entry) {
      // Handle directory entry - recursively transform contents
      transformedTree[name] = {
        directory: transformFileTree({
          fileTree: entry.directory,
          matchPatterns,
          currentPath: fullPath,
        }),
      };
    } else {
      // Handle other entry types (shouldn't happen, but keep for safety)
      transformedTree[name] = entry;
    }
  }

  return transformedTree;
}

export function injectSourceMetadata(code: string, fileName: string): string {
  const ast = parser.parse(code, {
    sourceType: "module",
    plugins: ["jsx", "typescript"],
    errorRecovery: true,
  });

  traverse(ast, {
    JSXOpeningElement(path) {
      const loc = path.node.loc;
      if (!loc) {
        return;
      }

      // Remove any existing replosource attributes
      path.node.attributes = path.node.attributes.filter((attr) => {
        if (
          t.isJSXAttribute(attr as any) &&
          t.isJSXIdentifier((attr as any).name) &&
          (attr as any).name.name === "replosource"
        ) {
          return false;
        }
        return true;
      });

      path.node.attributes.push(
        t.jsxAttribute(
          t.jsxIdentifier("replosource"),
          t.jsxExpressionContainer(
            t.objectExpression([
              t.objectProperty(
                t.identifier("fileName"),
                t.stringLiteral(fileName),
              ),
              t.objectProperty(
                t.identifier("lineNumber"),
                t.numericLiteral(loc.start.line),
              ),
              t.objectProperty(
                t.identifier("columnNumber"),
                t.numericLiteral(loc.start.column),
              ),
            ]),
          ),
        ),
      );
    },
  });

  const output = generate(ast, {
    retainLines: true,
    comments: true,
    compact: false,
    jsescOption: {
      quotes: "double",
      minimal: true,
    },
  });

  return output.code;
}

/**
 * Wraps file content with "use client" directive for Next.js client components
 * @param content - The original file content
 * @returns The content prefixed with "use client" directive
 */
export function wrapWithUseClient(content: string): string {
  // Check if "use client" already exists to avoid duplication
  if (
    content.trim().startsWith('"use client"') ||
    content.trim().startsWith("'use client'")
  ) {
    return content;
  }

  // Add "use client" directive at the beginning with a newline
  return `"use client"\n\n${content}`;
}
